import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  MapPin,
  RefreshCw,
  Snowflake,
  Thermometer,
} from 'lucide-react';
import {
  getHaccpTemperatureLocations,
  getHaccpTemperatureSummary,
  getHaccpConfigurationLocations,
  getHaccpNegativeContext,
  getHaccpPositiveContext,
} from './useHaccpConfigurations';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { canConfigureHaccp } from './haccpAccess';
import { apiFetch } from '@/lib/supabaseClient';
import {
  temperatureEquipmentDisplayName,
  temperatureEquipmentForDisplay,
} from './temperatureSetup';
import './haccp.css';

const COUNT_ITEMS = [
  { key: 'due', label: 'Da fare', Icon: Clock3, tone: 'due' },
  { key: 'non_compliant', label: 'Anomalie', Icon: AlertCircle, tone: 'alert' },
  { key: 'completed', label: 'Completati', Icon: CheckCircle2, tone: 'complete' },
];

function formatLocalDate(date, timezone) {
  if (!date) return 'Data non disponibile';
  const parsed = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return 'Data non disponibile';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: timezone || 'UTC',
    }).format(parsed);
  } catch {
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(parsed);
  }
}

function displayTemperature(value) {
  const text = String(value);
  const number = Number(text.replace(',', '.'));
  if (!Number.isFinite(number)) return text;
  const magnitude = Math.abs(number);
  const formatted = Number.isInteger(magnitude) ? String(magnitude) : magnitude.toString();
  if (number < 0) return `−${formatted}`;
  if (number > 0) return `+${formatted}`;
  return '0';
}

function configurationSnapshot(configuration) {
  if (!configuration) return null;
  return {
    id: configuration.id,
    revision: configuration.revision,
    status: configuration.status,
    modules: Array.isArray(configuration.modules)
      ? configuration.modules.map((module) => ({
        code: module.code,
        selected: module.selected,
      }))
      : [],
    equipment: Array.isArray(configuration.equipment)
      ? configuration.equipment.map((item) => ({
        id: item.id,
        kind: item.kind,
        number: item.number,
        label: item.label || null,
      }))
      : [],
  };
}

function DashboardSkeleton() {
  return (
    <main className="haccp-page haccp-dashboard" role="status" aria-live="polite" aria-label="Caricamento attività HACCP">
      <div className="haccp-skeleton haccp-skeleton-heading" />
      <div className="haccp-skeleton haccp-skeleton-banner" />
      <div className="haccp-skeleton haccp-skeleton-row" />
    </main>
  );
}

function TemperatureModuleCard({
  summary,
  locationName,
  locationUrl,
}) {
  const equipment = temperatureEquipmentForDisplay(summary.equipment || []);
  return (
    <article
      className="haccp-dashboard-module haccp-dashboard-temperature-module"
      data-testid="haccp-dashboard-temperature"
      data-module-code="TEMPERATURES"
    >
      <Link
        to={locationUrl}
        className="haccp-dashboard-module-link"
        aria-label={`Apri controlli temperatura per ${summary.location_name || locationName}`}
      >
        <div className="haccp-dashboard-module-head">
          <span className="haccp-dashboard-module-symbol" aria-hidden="true"><Thermometer size={22} /></span>
          <div>
            <span className="haccp-dashboard-module-kicker">Modulo operativo</span>
            <h2>Temperature</h2>
            <p>Controlli delle attrezzature del freddo e stato delle registrazioni di oggi.</p>
          </div>
          <span className="haccp-dashboard-module-arrow" aria-hidden="true"><ArrowRight size={17} /></span>
        </div>
        <div className="haccp-dashboard-stats" aria-label="Conteggi dei controlli di oggi">
          {COUNT_ITEMS.map(({ key, label, Icon, tone }) => (
            <div
              className={`haccp-dashboard-stat ${tone === 'alert' ? 'is-alert' : ''} ${tone === 'complete' ? 'is-complete' : ''}`}
              key={key}
            >
              <span><Icon size={13} aria-hidden="true" /> {label}</span>
              <b>{Number(summary.counts?.[key] ?? 0)}</b>
            </div>
          ))}
        </div>
      </Link>
      {equipment.length > 0 && (
        <div className="haccp-dashboard-temperature-equipment" aria-label="Attrezzature del locale">
          {equipment.map((item) => {
            const checks = (summary.items || []).filter((check) => check.equipment_id === item.equipment_id);
            const activeCheck = checks.find((check) => ['DUE', 'OVERDUE'].includes(check.status));
            const nextCheck = activeCheck || checks.find((check) => check.status === 'UPCOMING');
            const latest = checks
              .filter((check) => check.record)
              .sort((left, right) => String(right.record.checked_at).localeCompare(String(left.record.checked_at)))[0];
            const target = checks[0];
            const targetMinimum = target?.operational_target_min_temperature_c;
            const targetMaximum = target?.operational_target_max_temperature_c;
            const targetLabel = targetMinimum != null && targetMaximum != null
              ? `Previsto ${displayTemperature(targetMinimum)}° / ${displayTemperature(targetMaximum)}°C`
              : 'Secondo il piano del locale';
            const actionUrl = `${locationUrl}&equipment_id=${encodeURIComponent(item.equipment_id)}`;
            const lastActivity = latest
              ? `Ultimo controllo ${latest.record.temperature_c}°C`
              : nextCheck?.scheduled_time_local
                ? `Prossimo controllo ${nextCheck.scheduled_time_local}`
                : '';
            return (
              <article className="haccp-dashboard-temperature-equipment-row" key={item.equipment_id}>
                <div className="haccp-dashboard-temperature-equipment-copy">
                  <strong>{temperatureEquipmentDisplayName(item.kind, item.displayNumber, item.label)}</strong>
                  <span>{targetLabel}</span>
                  {lastActivity && <small>{lastActivity}</small>}
                </div>
                <Link
                  className="haccp-button haccp-button-secondary"
                  to={actionUrl}
                  data-testid={`temperature-equipment-action-${item.equipment_id}`}
                >
                  {activeCheck ? 'Fai foto' : 'Vedi controllo'}
                </Link>
              </article>
            );
          })}
        </div>
      )}
    </article>
  );
}

function PositiveBlastChillingModuleCard({ locationId, locationName }) {
  return (
    <Link
      to={`/haccp/abbattimento-positivo?location_id=${encodeURIComponent(locationId)}`}
      className="haccp-dashboard-module"
      data-testid="haccp-dashboard-positive-blast-chilling"
      data-module-code="BLAST_CHILLING_POSITIVE"
      aria-label={`Apri abbattimento positivo per ${locationName}`}
    >
      <div className="haccp-dashboard-module-head">
        <span className="haccp-dashboard-module-symbol" aria-hidden="true"><Snowflake size={22} /></span>
        <div>
          <span className="haccp-dashboard-module-kicker">Modulo operativo</span>
          <h2>Abbattimento positivo</h2>
          <p>Registra fasi e temperature secondo la procedura approvata per questo locale.</p>
        </div>
        <span className="haccp-dashboard-module-arrow" aria-hidden="true"><ArrowRight size={17} /></span>
      </div>
      <div className="haccp-dashboard-module-foot"><MapPin size={14} /> {locationName} · Apri ciclo e storico</div>
    </Link>
  );
}

function NegativeBlastChillingModuleCard({ locationId, locationName }) {
  return (
    <Link
      to={`/haccp/abbattimento-negativo?location_id=${encodeURIComponent(locationId)}`}
      className="haccp-dashboard-module"
      data-testid="haccp-dashboard-negative-blast-chilling"
      data-module-code="BLAST_CHILLING_NEGATIVE"
      aria-label={`Apri abbattimento negativo per ${locationName}`}
    >
      <div className="haccp-dashboard-module-head">
        <span className="haccp-dashboard-module-symbol" aria-hidden="true"><Snowflake size={22} /></span>
        <div>
          <span className="haccp-dashboard-module-kicker">Modulo operativo</span>
          <h2>Abbattimento negativo</h2>
          <p>Registra le fasi secondo la procedura negativa approvata per questo locale.</p>
        </div>
        <span className="haccp-dashboard-module-arrow" aria-hidden="true"><ArrowRight size={17} /></span>
      </div>
      <div className="haccp-dashboard-module-foot"><MapPin size={14} /> {locationName} · Apri ciclo e storico</div>
    </Link>
  );
}

export default function HaccpDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useEffectiveMe();
  const isConfigurator = canConfigureHaccp(me);
  const isGlobalCeo = me?.is_global === true && (me?.role_codes || []).includes('CEO');
  const requestedLocationId = searchParams.get('location_id');
  const [tenantOptions, setTenantOptions] = useState([]);
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [tenantContextLoaded, setTenantContextLoaded] = useState(!isGlobalCeo);
  const [tenantContextLoading, setTenantContextLoading] = useState(false);
  const [tenantContextError, setTenantContextError] = useState(false);
  const [tenantContextRetry, setTenantContextRetry] = useState(0);
  const [tenantSwitchBusy, setTenantSwitchBusy] = useState(false);
  const [tenantSwitchError, setTenantSwitchError] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsTenantId, setLocationsTenantId] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState(false);
  const [locationsRetry, setLocationsRetry] = useState(0);
  const [summary, setSummary] = useState(null);
  const [positiveModuleAvailable, setPositiveModuleAvailable] = useState(false);
  const [positiveModuleLocationId, setPositiveModuleLocationId] = useState(null);
  const [negativeModuleAvailable, setNegativeModuleAvailable] = useState(false);
  const [negativeModuleLocationId, setNegativeModuleLocationId] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(false);
  const [summaryRetry, setSummaryRetry] = useState(0);
  const tenantRequest = useRef(0);
  const locationsRequest = useRef(0);
  const summaryRequest = useRef(0);
  const positiveContextRequest = useRef(0);
  const negativeContextRequest = useRef(0);

  useEffect(() => {
    if (!isGlobalCeo) {
      setTenantOptions([]);
      setActiveTenantId(null);
      setTenantContextLoaded(true);
      setTenantContextLoading(false);
      setTenantContextError(false);
      setTenantSwitchError(false);
      return undefined;
    }

    let active = true;
    const requestId = ++tenantRequest.current;
    setTenantContextLoaded(false);
    setTenantContextLoading(true);
    setTenantContextError(false);
    Promise.all([
      apiFetch('/api/ceo/tenants'),
      apiFetch('/api/ceo/tenant-context'),
    ])
      .then(([responseTenants, context]) => {
        if (!Array.isArray(responseTenants)) throw new Error('Elenco aziende non valido');
        const nextTenants = responseTenants.map((tenant) => ({
          id: String(tenant?.id || ''),
          name: String(tenant?.display_name || tenant?.name || '').trim(),
        }));
        if (nextTenants.some((tenant) => !tenant.id || !tenant.name)
          || new Set(nextTenants.map((tenant) => tenant.id)).size !== nextTenants.length) {
          throw new Error('Elenco aziende non valido');
        }
        const nextTenantId = context?.active_tenant_id ? String(context.active_tenant_id) : null;
        if (nextTenantId && !nextTenants.some((tenant) => tenant.id === nextTenantId)) {
          throw new Error('Contesto aziendale non autorizzato');
        }
        if (active && requestId === tenantRequest.current) {
          setTenantOptions(nextTenants);
          setActiveTenantId(nextTenantId);
        }
      })
      .catch(() => {
        if (active && requestId === tenantRequest.current) {
          setTenantOptions([]);
          setActiveTenantId(null);
          setTenantContextError(true);
        }
      })
      .finally(() => {
        if (active && requestId === tenantRequest.current) {
          setTenantContextLoaded(true);
          setTenantContextLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [isGlobalCeo, tenantContextRetry]);

  const visibleLocations = useMemo(
    () => (isGlobalCeo && String(locationsTenantId || '') !== String(activeTenantId || '')
      ? []
      : locations),
    [locations, locationsTenantId, activeTenantId, isGlobalCeo],
  );
  const selectedLocation = useMemo(
    () => visibleLocations.find((location) => String(location.location_id) === String(requestedLocationId))
      || visibleLocations[0]
      || null,
    [visibleLocations, requestedLocationId],
  );
  useEffect(() => {
    let active = true;
    const requestId = ++positiveContextRequest.current;
    const selectedId = selectedLocation?.location_id ? String(selectedLocation.location_id) : null;
    setPositiveModuleAvailable(false);
    setPositiveModuleLocationId(selectedId);
    if (!selectedId) return () => { active = false; };
    getHaccpPositiveContext(selectedId)
      .then((context) => {
        if (active && requestId === positiveContextRequest.current
          && String(context.location_id) === selectedId) {
          setPositiveModuleAvailable(context.available === true);
        }
      })
      .catch(() => {
        if (active && requestId === positiveContextRequest.current) setPositiveModuleAvailable(false);
      });
    return () => { active = false; };
  }, [selectedLocation?.location_id]);
  useEffect(() => {
    let active = true;
    const requestId = ++negativeContextRequest.current;
    const selectedId = selectedLocation?.location_id ? String(selectedLocation.location_id) : null;
    setNegativeModuleAvailable(false);
    setNegativeModuleLocationId(selectedId);
    if (!selectedId) return () => { active = false; };
    getHaccpNegativeContext(selectedId)
      .then((context) => {
        if (active && requestId === negativeContextRequest.current
          && String(context.location_id) === selectedId) {
          setNegativeModuleAvailable(context.available === true);
        }
      })
      .catch(() => {
        if (active && requestId === negativeContextRequest.current) setNegativeModuleAvailable(false);
      });
    return () => { active = false; };
  }, [selectedLocation?.location_id]);
  const hasActiveTemperatureModule = !isConfigurator || Boolean(
    selectedLocation?.active_configuration?.modules?.some(
      (module) => module.code === 'TEMPERATURES' && module.selected,
    ),
  );

  useEffect(() => {
    let active = true;
    const requestId = ++locationsRequest.current;
    if (isGlobalCeo && (!tenantContextLoaded || tenantContextLoading || tenantContextError || !activeTenantId)) {
      setLocations([]);
      setLocationsTenantId(null);
      setLocationsLoading(false);
      setLocationsError(false);
      return () => {
        active = false;
      };
    }
    setLocationsLoading(true);
    setLocationsError(false);
    setLocations([]);
    const requestLocations = isConfigurator
      ? getHaccpConfigurationLocations
      : getHaccpTemperatureLocations;
    requestLocations()
      .then((response) => {
        if (!response || !Array.isArray(response.locations)) {
          throw new Error('Risposta locali non valida');
        }
        if (isGlobalCeo && response.locations.some(
          (location) => String(location?.tenant_id || '') !== String(activeTenantId),
        )) {
          throw new Error('Risposta fuori dal tenant autorizzato');
        }
        if (active && requestId === locationsRequest.current) {
          setLocations(response.locations.map((location) => ({
            location_id: location.location_id,
            tenant_id: location.tenant_id,
            company_name: isConfigurator ? location.company_name : undefined,
            location_name: location.location_name,
            active_configuration: isConfigurator
              ? configurationSnapshot(location.active_configuration)
              : null,
            draft_configuration: isConfigurator
              ? configurationSnapshot(location.draft_configuration)
              : null,
          })));
          setLocationsTenantId(isGlobalCeo ? String(activeTenantId) : null);
        }
      })
      .catch(() => {
        if (active && requestId === locationsRequest.current) {
          setLocations([]);
          setLocationsTenantId(null);
          setLocationsError(true);
        }
      })
      .finally(() => {
        if (active && requestId === locationsRequest.current) setLocationsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    isConfigurator,
    isGlobalCeo,
    activeTenantId,
    tenantContextLoaded,
    tenantContextLoading,
    tenantContextError,
    locationsRetry,
  ]);

  useEffect(() => {
    if (locationsLoading || locationsError || !visibleLocations.length) return;
    const authorizedMatch = visibleLocations.find(
      (location) => String(location.location_id) === String(requestedLocationId),
    );
    const nextLocation = authorizedMatch || visibleLocations[0];
    if (String(nextLocation.location_id) !== String(requestedLocationId)) {
      setSearchParams({ location_id: String(nextLocation.location_id) }, { replace: true });
    }
  }, [visibleLocations, locationsError, locationsLoading, requestedLocationId, setSearchParams]);

  useEffect(() => {
    let active = true;
    const locationId = selectedLocation?.location_id;
    const requestId = ++summaryRequest.current;
    if (!locationId || locationsLoading || locationsError || tenantSwitchBusy
      || (isConfigurator && !hasActiveTemperatureModule)) {
      setSummary(null);
      setSummaryError(false);
      setSummaryLoading(false);
      return () => {
        active = false;
      };
    }

    setSummary(null);
    setSummaryError(false);
    setSummaryLoading(true);
    getHaccpTemperatureSummary(locationId)
      .then((response) => {
        if (!response || String(response.location_id) !== String(locationId)
          || !response.counts || typeof response.operational_available !== 'boolean') {
          throw new Error('Riepilogo non valido');
        }
        if (active && requestId === summaryRequest.current) setSummary(response);
      })
      .catch(() => {
        if (active && requestId === summaryRequest.current) setSummaryError(true);
      })
      .finally(() => {
        if (active && requestId === summaryRequest.current) setSummaryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    selectedLocation?.location_id,
    locationsError,
    locationsLoading,
    summaryRetry,
    isConfigurator,
    hasActiveTemperatureModule,
    tenantSwitchBusy,
  ]);

  if (isGlobalCeo && !tenantContextLoaded) return <DashboardSkeleton />;
  if (!isGlobalCeo && locationsLoading && locations.length === 0) return <DashboardSkeleton />;

  const retryLocations = () => setLocationsRetry((value) => value + 1);
  const retrySummary = () => setSummaryRetry((value) => value + 1);
  const retryTenantContext = () => setTenantContextRetry((value) => value + 1);
  const handleTenantChange = async (nextTenantId) => {
    if (!nextTenantId || nextTenantId === activeTenantId || tenantSwitchBusy) return;
    if (!tenantOptions.some((tenant) => tenant.id === nextTenantId)) {
      setTenantSwitchError(true);
      return;
    }

    setTenantSwitchBusy(true);
    setTenantSwitchError(false);
    try {
      const context = await apiFetch('/api/ceo/tenant-context', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: nextTenantId }),
      });
      if (String(context?.active_tenant_id || '') !== nextTenantId) {
        throw new Error('Contesto aziendale non confermato');
      }
      setActiveTenantId(nextTenantId);
      setSearchParams({}, { replace: true });
    } catch {
      setTenantSwitchError(true);
    } finally {
      setTenantSwitchBusy(false);
    }
  };
  const locationUrl = selectedLocation
    ? `/haccp/temperature?location_id=${encodeURIComponent(selectedLocation.location_id)}`
    : '';

  return (
    <main className="haccp-page haccp-dashboard" data-testid="haccp-dashboard">
      <header className="haccp-page-header">
        <div className="haccp-dashboard-intro">
          <div className="haccp-eyebrow"><Snowflake size={15} /> Sicurezza alimentare</div>
          <h1>Operatività HACCP</h1>
          <p>Le attività operative disponibili per il locale selezionato, con lo stato dei controlli di oggi.</p>
        </div>
        <div className="haccp-header-mark" aria-hidden="true"><Thermometer size={26} /></div>
      </header>

      {isGlobalCeo && (
        <section className="haccp-dashboard-tenant-context" aria-label="Azienda e gruppo">
          <label className="haccp-field haccp-dashboard-tenant-select">
            <span>Azienda o gruppo</span>
            <select
              aria-label="Seleziona azienda HACCP"
              value={activeTenantId || ''}
              disabled={tenantContextLoading || tenantSwitchBusy || tenantOptions.length === 0}
              onChange={(event) => handleTenantChange(event.target.value)}
            >
              <option value="">
                {tenantOptions.length ? 'Seleziona un’azienda o un gruppo' : 'Nessuna azienda disponibile'}
              </option>
              {tenantOptions.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </label>
          {tenantSwitchBusy && <p role="status">Cambio azienda in corso…</p>}
          {tenantSwitchError && (
            <p className="haccp-dashboard-tenant-error" role="alert">
              Non è stato possibile cambiare azienda. Il contesto precedente resta attivo.
            </p>
          )}
        </section>
      )}

      {isGlobalCeo && tenantContextError ? (
        <section className="haccp-state-panel haccp-dashboard-error" role="alert">
          <span className="haccp-state-icon haccp-state-icon-error"><AlertCircle size={21} /></span>
          <div>
            <h2>Aziende non disponibili</h2>
            <p>Non è stato possibile caricare il perimetro autorizzato. Riprova tra poco.</p>
          </div>
          <button type="button" className="haccp-button haccp-button-secondary" onClick={retryTenantContext}>
            <RefreshCw size={15} /> Riprova
          </button>
        </section>
      ) : isGlobalCeo && !activeTenantId ? (
        <section className="haccp-state-panel haccp-dashboard-empty" role="status" data-testid="haccp-dashboard-select-tenant">
          <span className="haccp-state-icon"><MapPin size={21} /></span>
          <div>
            <h2>Seleziona un’azienda</h2>
            <p>Scegli un’azienda o un gruppo autorizzato per visualizzare i relativi locali HACCP.</p>
          </div>
        </section>
      ) : locationsError ? (
        <section className="haccp-state-panel haccp-dashboard-error" role="alert">
          <span className="haccp-state-icon haccp-state-icon-error"><AlertCircle size={21} /></span>
          <div>
            <h2>Locali non disponibili</h2>
            <p>Non è stato possibile recuperare i locali autorizzati. Riprova tra poco.</p>
          </div>
          <button type="button" className="haccp-button haccp-button-secondary" onClick={retryLocations}>
            <RefreshCw size={15} /> Riprova
          </button>
        </section>
      ) : locationsLoading && visibleLocations.length === 0 ? (
        <section role="status" aria-label="Caricamento locali autorizzati" aria-live="polite">
          <div className="haccp-skeleton haccp-skeleton-banner" />
          <div className="haccp-skeleton haccp-skeleton-row" />
        </section>
      ) : visibleLocations.length === 0 ? (
        <section className="haccp-state-panel haccp-dashboard-empty" data-testid="haccp-dashboard-no-locations">
          <span className="haccp-state-icon"><MapPin size={21} /></span>
          <div>
            <h2>Nessun locale disponibile</h2>
            <p>{isConfigurator
              ? 'Non risultano locali nel perimetro HACCP autorizzato per questo account.'
              : 'Non risultano locali assegnati con un piano Temperature operativo.'}</p>
          </div>
        </section>
      ) : selectedLocation ? (
        <>
          <section className="haccp-dashboard-location-bar" aria-label="Locale e data operativa">
            <div className="haccp-dashboard-location-copy">
              <span className="haccp-dashboard-location-icon"><MapPin size={18} /></span>
              <span>
                <small>Locale selezionato</small>
                <small>{selectedLocation.company_name || 'Locale selezionato'}</small>
                <strong>{selectedLocation.location_name}</strong>
              </span>
            </div>
            {visibleLocations.length > 1 ? (
              <label className="haccp-field haccp-dashboard-location-select">
                <span className="sr-only">Seleziona locale autorizzato</span>
                <select
                  aria-label="Seleziona locale autorizzato"
                  value={String(selectedLocation.location_id)}
                  onChange={(event) => setSearchParams({ location_id: event.target.value })}
                >
                  {visibleLocations.map((location) => (
                    <option key={location.location_id} value={String(location.location_id)}>
                      {location.company_name ? `${location.company_name} · ` : ''}{location.location_name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              summary?.local_date && (
                <span className="haccp-dashboard-date">
                  <CalendarDays size={15} /> {formatLocalDate(summary.local_date, summary.timezone)}
                </span>
              )
            )}
          </section>

          {tenantSwitchBusy ? (
            <section role="status" aria-live="polite" className="haccp-state-panel">
              Cambio azienda in corso…
            </section>
          ) : !hasActiveTemperatureModule ? (
            isConfigurator ? null : (
              <section className="haccp-state-panel haccp-dashboard-empty" role="status" data-testid="haccp-dashboard-temperature-unavailable">
                <span className="haccp-state-icon"><Thermometer size={21} /></span>
                <div>
                  <h2>Nessuna attività temperatura disponibile</h2>
                  <p>Per questo locale non risultano controlli operativi.</p>
                </div>
              </section>
            )
          ) : summaryLoading ? (
            <section role="status" aria-label="Caricamento riepilogo temperature" aria-live="polite">
              <div className="haccp-skeleton haccp-skeleton-banner" />
              <div className="haccp-skeleton haccp-skeleton-row" />
            </section>
          ) : summaryError ? (
            <section className="haccp-state-panel haccp-dashboard-error" role="alert">
              <span className="haccp-state-icon haccp-state-icon-error"><AlertCircle size={21} /></span>
              <div>
                <h2>Riepilogo non disponibile</h2>
                <p>Non è stato possibile caricare lo stato dei controlli per questo locale.</p>
              </div>
              <button type="button" className="haccp-button haccp-button-secondary" onClick={retrySummary}>
                <RefreshCw size={15} /> Riprova
              </button>
            </section>
          ) : !summary?.operational_available ? (
            isConfigurator ? null : (
              <section className="haccp-state-panel haccp-dashboard-empty" role="status" data-testid="haccp-dashboard-temperature-unavailable">
                <span className="haccp-state-icon"><Thermometer size={21} /></span>
                <div>
                  <h2>Nessuna attività temperatura disponibile</h2>
                  <p>Al momento non risultano controlli temperatura operativi per questo locale.</p>
                </div>
              </section>
            )
          ) : null}

          {!tenantSwitchBusy && (
            <>
              {visibleLocations.length > 1 && summary?.local_date && (
                <p className="haccp-dashboard-caption haccp-dashboard-date">
                  <CalendarDays size={14} /> {formatLocalDate(summary.local_date, summary.timezone)}
                </p>
              )}
              <section
                className="haccp-dashboard-module-grid"
                data-module-count={(summary?.operational_available ? 1 : 0)
                  + (positiveModuleAvailable && String(positiveModuleLocationId || '') === String(selectedLocation.location_id) ? 1 : 0)
                  + (negativeModuleAvailable && String(negativeModuleLocationId || '') === String(selectedLocation.location_id) ? 1 : 0)}
                aria-label={`Moduli HACCP per ${selectedLocation.location_name}`}
              >
                {summary?.operational_available && (
                  <TemperatureModuleCard
                    summary={summary}
                    locationName={selectedLocation.location_name}
                    locationUrl={locationUrl}
                  />
                )}
                {positiveModuleAvailable
                  && String(positiveModuleLocationId || '') === String(selectedLocation.location_id) && (
                    <PositiveBlastChillingModuleCard
                      locationId={selectedLocation.location_id}
                      locationName={selectedLocation.location_name}
                    />
                  )}
                {negativeModuleAvailable
                  && String(negativeModuleLocationId || '') === String(selectedLocation.location_id) && (
                    <NegativeBlastChillingModuleCard
                      locationId={selectedLocation.location_id}
                      locationName={selectedLocation.location_name}
                    />
                  )}
              </section>
            </>
          )}
        </>
      ) : null}
    </main>
  );
}
